import React, { useEffect, useRef, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    LinearProgress,
    Typography,
    Box,
    Alert,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
} from "@mui/material";
import shipmentService from "../../services/shipmentService";

// Kicks off the scan the moment it's opened and streams live progress —
// used from both AdminShipments and AdminUnclaimedPackages.
const ScanUnclaimedMatchesDialog = ({ open, onClose, onComplete }) => {
    const [progress, setProgress] = useState({ checked: 0, total: 0, matched: 0 });
    const [status, setStatus] = useState("idle"); // idle | scanning | done | error
    const [error, setError] = useState("");
    const [matchedPackages, setMatchedPackages] = useState([]);
    const startedRef = useRef(false);

    useEffect(() => {
        if (!open) {
            startedRef.current = false;
            setProgress({ checked: 0, total: 0, matched: 0 });
            setStatus("idle");
            setError("");
            setMatchedPackages([]);
            return;
        }
        if (startedRef.current) return;
        startedRef.current = true;

        setStatus("scanning");
        shipmentService
            .scanForUnclaimedMatches((evt) => {
                setProgress({
                    checked: evt.checked ?? 0,
                    total: evt.total ?? 0,
                    matched: evt.matched ?? 0,
                });
            })
            .then((final) => {
                setProgress((p) => ({
                    ...p,
                    checked: final?.checkedCount ?? p.checked,
                    matched: final?.matchedCount ?? p.matched,
                }));
                setMatchedPackages(final?.matchedPackages || []);
                setStatus("done");
                onComplete?.(final);
            })
            .catch((err) => {
                setError(err.message || "Failed to scan for matches");
                setStatus("error");
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const pct = progress.total > 0 ? Math.round((progress.checked / progress.total) * 100) : 0;

    return (
        <Dialog
            open={open}
            onClose={status === "scanning" ? undefined : onClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>Scanning for Unclaimed Package Matches</DialogTitle>
            <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {status === "error" ? (
                    <Alert severity="error">{error}</Alert>
                ) : (
                    <>
                        <LinearProgress
                            variant={progress.total ? "determinate" : "indeterminate"}
                            value={pct}
                            sx={{ height: 8, borderRadius: 4 }}
                        />
                        <Typography variant="body2" color="text.secondary" textAlign="center">
                            {progress.total
                                ? `${progress.checked} of ${progress.total} tracking numbers scanned`
                                : "Preparing scan…"}
                        </Typography>
                        <Typography variant="body2" fontWeight={600} textAlign="center">
                            {progress.matched} package{progress.matched === 1 ? "" : "s"} matched
                        </Typography>
                        {status === "done" && (
                            <>
                                <Alert severity="success">
                                    Scan complete. Matched packages have been removed from the unclaimed list.
                                </Alert>
                                {matchedPackages.length > 0 && (
                                    <Box sx={{ overflowX: "auto", maxHeight: 320, overflowY: "auto" }}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Tracking #</TableCell>
                                                    <TableCell>Customer</TableCell>
                                                    <TableCell>Shipment</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {matchedPackages.map((p) => (
                                                    <TableRow key={p.packageId}>
                                                        <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                                                            {p.trackingNumber}
                                                        </TableCell>
                                                        <TableCell>{p.customerName || "—"}</TableCell>
                                                        <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                                                            {p.shipmentTrackingNumber || "—"}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </Box>
                                )}
                            </>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={status === "scanning"} variant="contained">
                    {status === "scanning" ? "Scanning…" : "Close"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ScanUnclaimedMatchesDialog;
